import React, {
    useEffect,
    useState,
    useRef
} from 'react'

import { connect } from 'react-redux'

import Icon from 'components/icon/icon'
import LoadMore from 'components/load-more/load-more'
import AlertBanner from 'components/alerts/alert-banner'

import {
    getChatLinks
} from 'state/actions'

import {
    formatByTimeZone,
    formatStandardDateTime,
} from 'utils'

const URL_REGEX = /https?:\/\/[^\s<>"']+/g

// Messages can be HTML (with anchors and mentions) or plain text, so pull URLs from both.
const extractLinks = messageText => {
    if (!messageText) {
        return []
    }

    const doc = new DOMParser().parseFromString(messageText, 'text/html')

    const urls = Array.from(doc.querySelectorAll('a[href]'))
        .map(anchor => anchor.getAttribute('href'))
        .filter(href => /^https?:\/\//.test(href))

    const textUrls = (doc.body.textContent || '').match(URL_REGEX) || []

    textUrls.forEach(url => {
        const trimmedUrl = url.replace(/[.,!?;:)]+$/, '')

        if (!urls.includes(trimmedUrl)) {
            urls.push(trimmedUrl)
        }
    })

    return [...new Set(urls)]
}

const BigChatLinks = ({
    chat,
    getChatLinks,
    allUsers
}) => {
    const [messages, setMessages] = useState([])
    const [total, setTotal] = useState(0)
    const [offset, setOffset] = useState(0)
    const [isLoadingInitial, setIsLoadingInitial] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const chatObjectId = useRef(chat ? chat.id : null)

    const fetch = (fromOffset, isReset = false) => {
        const chatId = chat.id

        setIsLoadingMore(true)

        getChatLinks(chatId, fromOffset, ({
            messages: fetchedMessages,
            totals
        }) => {
            if (chatId !== chatObjectId.current) {
                return
            }

            setIsLoadingMore(false)
            setIsLoadingInitial(false)

            setMessages(isReset ? fetchedMessages : [...messages, ...fetchedMessages])
            setTotal(totals)
            setOffset(fromOffset + 10)
        })
    }

    useEffect(() => {
        fetch(0, true)
    }, [])

    useEffect(() => {
        if (chat && chat.id && chat.id !== chatObjectId.current) {
            chatObjectId.current = chat.id

            setIsLoadingInitial(true)
            setMessages([])
            setTotal(0)
            setOffset(0)

            fetch(0, true)
        }
    }, [chat])

    const links = messages.reduce((allLinks, message) => {
        extractLinks(message.message_text).forEach((url, index) => {
            allLinks.push({
                key: `${message.id}-${index}`,
                url,
                message
            })
        })

        return allLinks
    }, [])

    const _links = isLoadingInitial ? new Array(4).fill(null).map((_, index) => ({
        key: index,
        isLoading: true
    })) : links

    return (
        <div className={!_links.length ? 'no-uploads' : ''}>
            <ul>
                {_links.length ?
                    _links.map(({ key, url, message, isLoading }, index) => {
                        const sender = (!isLoading && allUsers) ? allUsers[message.sender_id] : null

                        const meta = isLoading ? '2018-11-14 10:48:28' : [
                            sender ? `${sender.first_name} ${sender.last_name}` : null,
                            formatStandardDateTime(formatByTimeZone(message.created_at, null, true), null, false, ' •', true)
                        ].filter(Boolean).join(' • ')

                        return (
                            <li
                                key={key}
                                className={isLoading ? 'loading link-item' : 'link-item'}
                                style={{
                                    animationDelay: (index / 10) + 's'
                                }}
                            >
                                <a
                                    href={isLoading ? undefined : url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    title={url}
                                >
                                    <span className='file-display'>
                                        {!isLoading && <Icon name='link' />}
                                    </span>
                                    <div>
                                        <p>
                                            {isLoading ? 'XXXXXXX XXXXXXXX' : url}
                                        </p>
                                        <span>
                                            {meta}
                                        </span>
                                    </div>
                                </a>
                            </li>
                        )
                    })
                    :
                    <AlertBanner
                        title={`No links`}
                        body={`No links have been shared in this chat.`}
                    />
                }
                {(total > offset && !isLoadingInitial) &&
                    <li>
                        <LoadMore
                            canLoadMore={total > offset}
                            onClick={() => fetch(offset)}
                            type={'links'}
                            isLoading={isLoadingMore}
                        />
                    </li>
                }
            </ul>
        </div>
    )
}

const mapStateToProps = state => {
    return {
        allUsers: state.account.entities.users
    }
}

export default connect(mapStateToProps, {
    getChatLinks
})(BigChatLinks)
